import React from 'react'
import {
  StyledTabs,
  StyledTab,
  Container,
  Title,
  Description,
  TabsContainer,
  HorizontalLine,
} from './NavigationTabs.styled'

interface NavigationTab {
  /**
   * The label to display in the tab
   */
  label: string
  /**
   * The path this tab should navigate to
   */
  path: string
  /**
   * Optional aria-label for the tab. If not provided, label will be used
   */
  ariaLabel?: string
  /**
   * Optional description text for this tab
   */
  description?: string
}

interface NavigationTabsProps {
  /**
   * Array of tab configurations
   */
  tabs: NavigationTab[]
  /**
   * The currently selected tab value
   */
  selectedTab: number
  /**
   * Callback fired when a tab is clicked
   */
  onTabChange: (index: number) => void
  /**
   * The page title, displayed above the tabs
   */
  title: string
}

/**
 * NavigationTabs component provides a consistent navigation pattern across the application.
 * It displays a row of tabs with a pill-style design and handles navigation between different views.
 *
 * Keyboard navigation:
 * - Tab: Move focus to the next tab without selecting it
 * - Enter/Space: Select the focused tab
 * - Left/Right Arrow: Move focus between tabs without selecting
 * - Home: Move focus to the first tab
 * - End: Move focus to the last tab
 */
export const NavigationTabs: React.FC<NavigationTabsProps> = ({
  tabs,
  selectedTab,
  onTabChange,
  title,
}) => {
  const selectedTabDescription = tabs[selectedTab]?.description

  return (
    <Container>
      <Title>{title}</Title>
      <TabsContainer>
        <StyledTabs
          value={selectedTab}
          onChange={(_, newValue) => onTabChange(newValue)}
        >
          {tabs.map((tab) => (
            <StyledTab
              key={tab.path}
              label={tab.label}
              aria-label={tab.ariaLabel}
            />
          ))}
        </StyledTabs>
        <HorizontalLine />
      </TabsContainer>
      {selectedTabDescription && (
        <Description>{selectedTabDescription}</Description>
      )}
    </Container>
  )
}
